// User-selectable DISPLAY timezone for the portal UI (client-side only).
//
// Single source of truth for rendering absolute date/times. The chosen IANA
// timezone is persisted in localStorage (no backend). Every visible date/time
// renders in this zone WITH the zone abbreviation + UTC offset computed for the
// actual displayed instant (DST-correct), and carries a UTC hover tooltip.
//
// SSR/test-safe: all `localStorage` / `Intl` access is defensively guarded so
// this module imports cleanly in a bare Node test runner.

import { h } from '../components/dom.js';

const STORAGE_KEY = 'vid_display_tz';

// --- storage (guarded) ----------------------------------------------------

function readStored(): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const v = localStorage.getItem(STORAGE_KEY);
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

function writeStored(tz: string): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, tz);
  } catch {
    /* ignore quota / disabled storage */
  }
}

// The browser's current IANA zone, or 'UTC' if unavailable.
export function browserTimeZone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || 'UTC';
  } catch {
    return 'UTC';
  }
}

// getUserTimeZone returns the stored display tz, else the browser tz, else UTC.
export function getUserTimeZone(): string {
  return readStored() || browserTimeZone();
}

// setUserTimeZone persists the user's explicit choice.
export function setUserTimeZone(tz: string): void {
  writeStored(tz);
}

// ensureTimeZoneInitialized stores the detected browser tz as the default the
// first time only — never overwrites an existing user choice. Idempotent/cheap.
export function ensureTimeZoneInitialized(): void {
  if (readStored()) return;
  writeStored(browserTimeZone());
}

// --- formatting ------------------------------------------------------------

function toDate(iso: string | number | Date): Date | null {
  if (iso instanceof Date) return Number.isNaN(iso.getTime()) ? null : iso;
  if (typeof iso === 'number') {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof iso === 'string') {
    if (!iso) return null;
    const t = Date.parse(iso);
    return Number.isNaN(t) ? null : new Date(t);
  }
  return null;
}

// Compute the UTC offset (in minutes) of `date` in `tz`, DST-correct. Works by
// reading the wall-clock parts in the zone and diffing against the same instant
// interpreted as UTC. Positive = east of UTC (e.g. +330 for IST).
function offsetMinutes(date: Date, tz: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(date);
  const get = (t: string): number => Number(parts.find((p) => p.type === t)?.value);
  let hour = get('hour');
  if (hour === 24) hour = 0; // some engines emit 24 for midnight
  const asUTC = Date.UTC(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'));
  return Math.round((asUTC - date.getTime()) / 60000);
}

// tzOffsetMinutes is the exported alias of offsetMinutes: the UTC offset (in
// minutes) of `instant` in `tz`, DST-correct. Positive = east of UTC.
export function tzOffsetMinutes(instant: Date, tz: string): number {
  return offsetMinutes(instant, tz);
}

// Format an offset in minutes as `UTC`, `UTC-8`, `UTC+5:30`, `UTC+5:45`.
function formatOffset(mins: number): string {
  if (mins === 0) return 'UTC';
  const sign = mins > 0 ? '+' : '-';
  const abs = Math.abs(mins);
  const hh = Math.floor(abs / 60);
  const mm = abs % 60;
  return mm === 0 ? `UTC${sign}${hh}` : `UTC${sign}${hh}:${String(mm).padStart(2, '0')}`;
}

// The short zone abbreviation (PST/PDT/IST/...) for `date` in `tz`. Falls back
// to the numeric offset label if the engine only yields a GMT+X form.
function zoneAbbrev(date: Date, tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour: '2-digit', timeZoneName: 'short',
    }).formatToParts(date);
    const name = parts.find((p) => p.type === 'timeZoneName')?.value;
    if (name && !/^(GMT|UTC)?[+-]/.test(name)) return name;
    return '';
  } catch {
    return '';
  }
}

export interface FormatOpts {
  tz?: string;        // explicit zone override (for testability / determinism)
  dateOnly?: boolean; // omit the time portion
}

// formatInZone renders an instant as e.g. `Jul 20, 2026, 08:15 PM PDT (UTC-7)`
// in the user's (or an explicit) display zone. Returns '' for missing/bad input.
// dateOnly => `Jul 20, 2026 PDT (UTC-7)` style (date + zone label, no time).
export function formatInZone(iso: string | number | Date, opts: FormatOpts = {}): string {
  const date = toDate(iso);
  if (!date) return '';
  const tz = opts.tz || getUserTimeZone();
  try {
    const dateFmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, year: 'numeric', month: 'short', day: 'numeric',
    }).format(date);
    const off = formatOffset(offsetMinutes(date, tz));
    const abbr = zoneAbbrev(date, tz);
    const label = abbr ? `${abbr} (${off})` : `(${off})`;
    if (opts.dateOnly) return `${dateFmt} ${label}`;
    const timeFmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: true,
    }).format(date);
    return `${dateFmt}, ${timeFmt} ${label}`;
  } catch {
    // Unknown/invalid tz — fall back to UTC rendering rather than throwing.
    if (tz !== 'UTC') return formatInZone(date, { ...opts, tz: 'UTC' });
    return date.toISOString();
  }
}

// utcTooltip returns an unambiguous UTC string for a `title` attribute, e.g.
// `2026-07-21 03:15 UTC`. Returns '' for missing/bad input.
export function utcTooltip(iso: string | number | Date): string {
  const date = toDate(iso);
  if (!date) return '';
  const p = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(date);
  const g = (t: string): string => p.find((x) => x.type === t)?.value || '';
  return `${g('year')}-${g('month')}-${g('day')} ${g('hour')}:${g('minute')} UTC`;
}

// dateTimeEl is the preferred convenience: a <span class="dt"> whose visible
// text is the zoned/abbrev/offset form and whose `title` is the UTC time, so the
// UTC-on-hover tooltip is automatic everywhere this is used. Returns '' text for
// missing input (caller can skip appending an empty element if desired).
export function dateTimeEl(iso: string | number | Date, opts: FormatOpts = {}): HTMLElement {
  return h('span', { class: 'dt', title: utcTooltip(iso) }, formatInZone(iso, opts));
}

// --- local-input <-> UTC conversion (for datetime-local pickers) -----------

// zonedInputToUtcISO takes a datetime-local string like "2026-07-27T14:30"
// INTERPRETED AS WALL-CLOCK TIME IN `tz` and returns the correct UTC ISO
// instant. Uses a two-pass wall-clock→UTC resolution so it stays correct across
// DST boundaries. Falls back to naive parsing if anything goes wrong.
export function zonedInputToUtcISO(localValue: string, tz?: string): string {
  const zone = tz || getUserTimeZone();
  try {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(localValue.trim());
    if (!m) return new Date(localValue).toISOString();
    const [, Y, Mo, D, h2, mi] = m.map(Number) as unknown as number[];
    // Guess: treat the wall-clock parts as if they were UTC.
    const guessMs = Date.UTC(Y, Mo - 1, D, h2, mi);
    // First-pass offset at the guessed instant, then correct.
    const off1 = offsetMinutes(new Date(guessMs), zone);
    let utcMs = guessMs - off1 * 60000;
    // Second pass: re-check the offset at the corrected instant (DST boundaries).
    const off2 = offsetMinutes(new Date(utcMs), zone);
    if (off2 !== off1) utcMs = guessMs - off2 * 60000;
    return new Date(utcMs).toISOString();
  } catch {
    return new Date(localValue).toISOString();
  }
}

// utcToZonedInputValue is the inverse: takes a UTC instant and returns the
// "YYYY-MM-DDTHH:mm" datetime-local string representing that instant's WALL
// CLOCK in `tz` (for seeding a datetime-local picker in the user's zone).
export function utcToZonedInputValue(iso: string | number | Date, tz?: string): string {
  const zone = tz || getUserTimeZone();
  const date = toDate(iso);
  if (!date) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(date);
    const g = (t: string): string => parts.find((p) => p.type === t)?.value || '';
    let hh = g('hour');
    if (hh === '24') hh = '00'; // some engines emit 24 for midnight
    return `${g('year')}-${g('month')}-${g('day')}T${hh}:${g('minute')}`;
  } catch {
    return '';
  }
}

// tzShortLabel returns e.g. "PDT (UTC-7)" for the CURRENT moment (or `at`) in
// the user's (or explicit) tz — for labeling a local-time input. Returns just
// "(UTC±N)" if no abbreviation is available.
export function tzShortLabel(tz?: string, at?: Date): string {
  const zone = tz || getUserTimeZone();
  const when = at || new Date();
  try {
    const off = formatOffset(offsetMinutes(when, zone));
    const abbr = zoneAbbrev(when, zone);
    return abbr ? `${abbr} (${off})` : `(${off})`;
  } catch {
    return '(UTC)';
  }
}

// tzOffsetLabel returns just the "UTC±N" portion for a zone at the current
// instant (DST-correct) — used to annotate each option in the Settings picker.
export function tzOffsetLabel(tz: string, at?: Date): string {
  try {
    return formatOffset(offsetMinutes(at || new Date(), tz));
  } catch {
    return 'UTC';
  }
}

// Curated common IANA zones, ordered roughly west→east, for the dropdown.
const CURATED_ZONES: string[] = [
  'UTC',
  'Pacific/Honolulu',
  'America/Anchorage',
  'America/Los_Angeles',
  'America/Denver',
  'America/Phoenix',
  'America/Chicago',
  'America/Mexico_City',
  'America/New_York',
  'America/Toronto',
  'America/Bogota',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'Atlantic/Azores',
  'Europe/London',
  'Europe/Lisbon',
  'Europe/Madrid',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Warsaw',
  'Europe/Athens',
  'Europe/Istanbul',
  'Europe/Moscow',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Africa/Nairobi',
  'Asia/Jerusalem',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Kathmandu',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Jakarta',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Sydney',
  'Pacific/Auckland',
];

// listTimeZones returns the option list for the Settings dropdown. Uses the full
// runtime list via Intl.supportedValuesOf when available (feature-detected),
// else the curated set. Always includes `ensure` (the current/browser zone) so
// the selected value is present even if uncommon.
export function listTimeZones(ensure?: string): string[] {
  let base: string[];
  try {
    const sv = (Intl as any).supportedValuesOf;
    base = typeof sv === 'function' ? (sv.call(Intl, 'timeZone') as string[]) : CURATED_ZONES.slice();
  } catch {
    base = CURATED_ZONES.slice();
  }
  const set = new Set(base);
  if (ensure && !set.has(ensure)) return [ensure, ...base];
  return base;
}
